import { PartialMessage, Timestamp } from '@bufbuild/protobuf';
import {
  PracticalProject,
  Resource_ResourceEnum,
} from '@protos/content_management/content.db_pb';
import { ProjectResourceInfo } from '@protos/learning_management/lms.assessment.common.apis_pb';
import type { GeneoIotProjectFile, GeneoIotProjectMeta } from './index';

/**
 * Turns the authored reference project stored on a `Practical` into the editor's
 * own file format, so a first open starts on the content team's blocks instead
 * of an empty workspace.
 *
 * The scalar fields are proto3 defaults when unset - `""` means "not authored",
 * not "board named empty string" - so they are dropped rather than handed to the
 * frame, which then applies its own defaults. `board` and `editorMode` are plain
 * strings on the wire and are narrowed by cast: the frame is the authority on
 * which values it knows.
 */
export const practicalProjectToProjectFile = (
  project: PracticalProject,
  /** Overrides the authored identity, e.g. so the copy is named for the group working on it. */
  meta?: GeneoIotProjectMeta
): GeneoIotProjectFile => ({
  version: project.version,
  meta: meta ?? {
    name: project.meta?.name || '',
    author: project.meta?.author || '',
    description: project.meta?.description || '',
  },
  board: project.board
    ? (project.board as GeneoIotProjectFile['board'])
    : undefined,
  workspace: {
    generatedCode: project.workspace?.generatedCode || '',
    xml: project.workspace?.xml || '',
  },
  editorMode: project.editorMode
    ? (project.editorMode as GeneoIotProjectFile['editorMode'])
    : undefined,
  savedAt: (project.savedAt
    ? new Timestamp(project.savedAt).toDate()
    : new Date()
  ).toISOString(),
});

/**
 * The reverse, for saving a project the editor emitted: a teacher's worked
 * solution on a practical, carried on the creation call.
 *
 * `board` and `editorMode` are optional in the editor's file and plain strings
 * on the wire, so an unset one becomes `""` - proto3 has no third state, and the
 * read side already reads `""` as "not authored". `savedAt` is the editor's own
 * clock; a file without one is stamped now, because the field is not optional.
 */
export const projectFileToPracticalProject = (
  project: GeneoIotProjectFile
): PartialMessage<PracticalProject> => ({
  board: project.board ?? '',
  editorMode: project.editorMode ?? '',
  meta: {
    name: project.meta?.name ?? '',
    author: project.meta?.author ?? '',
    description: project.meta?.description ?? '',
  },
  savedAt: Timestamp.fromDate(
    project.savedAt ? new Date(project.savedAt) : new Date()
  ),
  version: project.version ?? '',
  workspace: {
    generatedCode: project.workspace?.generatedCode ?? '',
    xml: project.workspace?.xml ?? '',
  },
});

/**
 * The teacher's worked solution off a fetched project's resources.
 *
 * A project carries exactly one practical, and only that resource can hold a
 * solution; `undefined` means the teacher saved none - including on every
 * project created before the field existed - so the editor falls back to the
 * content team's authored version alone.
 */
export const getTeacherSolution = (
  projectResources: ProjectResourceInfo[] | undefined
): PracticalProject | undefined =>
  projectResources?.find(
    (resource) =>
      resource.resourceType === Resource_ResourceEnum.RESOURCE_TYPE_PRACTICAL
  )?.teacherSolution;
